import type React from 'react';
import { useMemo, useState, useEffect } from 'react';
import { usePopper } from 'react-popper';
import { useGetGlobalAttributes, useGetXstyledProps } from '@elliemae/ds-legacy-props-helpers';
import { useConfiguredMergedProps } from './useConfiguredMergedProps.js';
import type { DSPopperJST } from '../react-desc-prop-types.js';
import { useOnClickOutside } from '../utils/hooks/useOnClickOutside.js';
import type { GlobalAttributesT, XstyledProps } from '@elliemae/ds-legacy-props-helpers';
interface UseDSPopperjsRT {
  props: DSPopperJST.InternalProps;
  popperElement: HTMLDivElement | null;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  isAnimating: boolean;
  setPopperElement: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  arrowElement: HTMLDivElement | null;
  setArrowElement: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  popoverHelper: ReturnType<typeof usePopper>;
  globalAttrs: GlobalAttributesT<Element>;
  xStyledAttrs: XstyledProps;
}

export const useConfigDSPopperjs = (appProps: DSPopperJST.Props): UseDSPopperjsRT => {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const props = useConfiguredMergedProps({ props: appProps, arrowElement });
  const { referenceElement, closeContextMenu, onClickOutside, modifiers, startPlacementPreference } = props;
  const popperExtraOpts = useMemo(
    () => ({ placement: startPlacementPreference, modifiers }),
    [modifiers, startPlacementPreference],
  );

  const popoverHelper = usePopper(referenceElement, popperElement, popperExtraOpts);

  const { showPopover, withoutAnimation } = props;

  useEffect(() => {
    if (showPopover && !withoutAnimation) {
      setIsAnimating(true);
    }
  }, [showPopover, withoutAnimation]);

  // Action ref setup
  useEffect(() => {
    if (props.actionRef) {
      props.actionRef.current.update = popoverHelper.update;
    }
  }, [props.actionRef, popoverHelper.update]);

  useOnClickOutside(popperElement as Node, (e) => {
    if (!referenceElement?.contains?.(e?.target as Node)) {
      onClickOutside(e);
      closeContextMenu();
    }
  });

  const globalAttrs = useGetGlobalAttributes(props);
  const xStyledAttrs = useGetXstyledProps(props);

  return useMemo(
    () => ({
      props,
      popperElement,
      setPopperElement,
      arrowElement,
      setArrowElement,
      popoverHelper,
      isAnimating,
      setIsAnimating,
      globalAttrs,
      xStyledAttrs,
    }),
    [props, popperElement, arrowElement, popoverHelper, isAnimating, globalAttrs, xStyledAttrs],
  );
};
